import React from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiAttachmentLine,
  RiCheckLine,
  RiEditLine,
  RiFileAddLine,
  RiFileList3Line,
  RiForbidLine,
  RiMoneyDollarCircleLine,
  RiRefund2Line,
  RiSendPlane2Line,
  RiToolsLine,
} from "react-icons/ri";
import { Button, SubtleButton } from "components/button";

import { dateFormatter } from "views/estimate/info";
import { getInvoiceActivity } from "./api";
import { TInvoiceActivityRow } from "./types";
import {
  buildDiff,
  compareInvoiceActivityTimelineRows,
  filterBundledInvoiceSendFieldChanges,
  formatDiffDisplayValue,
  getInvoiceActivityFriendly,
  getInvoiceActivityLabel,
  humanizeDiffPath,
  mergeLineItemReplacePairs,
  truncate,
} from "./utils";
import { NoStyleA } from "components/button";


type Props = {
  invoiceId: string;
};

/** Tax / fee / discount — combined $ / % cue in one slot */
function TaxFeeDiscountTimelineIcon() {
  return (
    <span
      data-timeline-icon
      className="inline-flex shrink-0 items-center justify-center gap-0 font-extrabold leading-none text-[#5d6d7e]"
      style={{ marginTop: "0.12rem", fontSize: "0.72rem", minWidth: "1.35rem", letterSpacing: "-0.02em" }}
      aria-hidden
    >
      <span>$</span>
      <span className="mx-[0.5px] text-[0.65rem] font-bold">/</span>
      <span>%</span>
    </span>
  );
}

/** Invoice fully paid — dollar with check overlay */
function PaidOffTimelineIcon() {
  return (
    <span
      data-timeline-icon
      className="relative inline-flex shrink-0 items-center justify-center text-[#5d6d7e]"
      style={{ width: "1.35rem", height: "1.35rem", marginTop: "0.1rem" }}
      aria-hidden
    >
      <RiMoneyDollarCircleLine className="text-[1.2rem]" />
      <RiCheckLine
        className="pointer-events-none absolute bottom-[-1px] right-[-3px] text-[0.78rem] text-green-600 drop-shadow-[0_0_1px_rgba(255,255,255,0.95)]"
        strokeWidth={3}
      />
    </span>
  );
}

function pickTimelineIcon(row: Pick<TInvoiceActivityRow, "type" | "description">) {
  const t = row.type || "";
  const d = row.description || "";

  // Match estimate timeline "created" icon behavior.
  if (/invoice\s+.*\s+created|was\s+created|\bcreated\b/i.test(d) || /\bcreate(d)?\b|creation/i.test(t)) {
    return RiFileAddLine;
  }

  const isWriteOff =
    t === "invoice-writeoff" ||
    t === "invoice-write-off" ||
    /write[-\s]?off/i.test(t) ||
    /write[-\s]?off/i.test(d);
  if (isWriteOff) return RiForbidLine;

  const isPaidOff =
    t === "invoice-paid-off" ||
    /paid\s*off/i.test(t) ||
    /invoice\s+paid\s+off/i.test(d);
  if (isPaidOff) return PaidOffTimelineIcon;

  const isPaymentRefund =
    /^invoice[_-]payment[_-]refund/i.test(t) ||
    /^invoice[_-]refund/i.test(t) ||
    /^payment[_-]refund/i.test(t) ||
    (/^payment[_-]/i.test(t) && /\brefund\b/i.test(t)) ||
    /\b(partial|full)\s+payment\s+refund\b/i.test(d) ||
    /\bpayment\s+refund\b/i.test(d);
  if (isPaymentRefund) return RiRefund2Line;

  const isPaymentActivity =
    /^invoice-payment-(apply|applied|unapply|unapplied|reallocate|reallocated)$/.test(t) ||
    /^payment-(apply|applied|unapply|unapplied)$/.test(t) ||
    /payment\s+(applied|unapplied|reallocated)/i.test(d);
  if (isPaymentActivity) return RiMoneyDollarCircleLine;

  const isTaxFeeDiscountType =
    /^invoice[_-](tax|fee|discount)[_-]/i.test(t) ||
    /\b(fee|discount|tax)\s+(added|deleted|removed|updated)\b/i.test(d) ||
    /\binvoice\s+\d+\s+(fee|discount|tax)\s+(added|deleted|removed|updated)\b/i.test(d);
  if (isTaxFeeDiscountType) return TaxFeeDiscountTimelineIcon;

  if (
    /^invoice[_-](send|resend|mark-sent)$/i.test(t) ||
    /\bInvoice\s+\d+\s+(sent|resent)\b/i.test(d) ||
    /\bInvoice\s+\d+\s+marked\s+sent\b/i.test(d) ||
    /\b(invoice\s+)?(sent|resent)\b.*\b(email|e-mail)\b/i.test(d) ||
    /\b(email|e-mail)\b.*\b(sent|delivered)\b/i.test(d)
  ) {
    return RiSendPlane2Line;
  }

  if (/line-/i.test(t)) return RiFileList3Line;
  if (/files/i.test(t)) return RiAttachmentLine;
  if (/template|billing|completion|due-date|creation-date|notes|send-flag/i.test(t)) return RiEditLine;
  return RiToolsLine;
}

const EachTimeline = styled.div`
  display: flex;
  padding: 0.5rem;
  gap: 0.5rem;
  align-items: flex-start;
  position: relative;
  > svg,
  > span[data-timeline-icon] {
    flex-shrink: 0;
    margin-top: 0.1rem;
    font-size: 1.2rem;
    color: #5d6d7e;
  }
  > div {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    > span:first-of-type {
      font-size: 0.9rem;
      color: black;
    }
    > span:not(:first-of-type) {
      color: grey;
      font-size: 0.8rem;
    }
  }
`;

const SkeletonRow = () => (
  <EachTimeline>
    <RiToolsLine style={{ opacity: 0.15 }} />
    <div>
      <span className="block h-4 max-w-[280px] rounded bg-slate-200 animate-pulse !text-transparent">
        &nbsp;
      </span>
      <span className="block h-3 max-w-[160px] rounded bg-slate-100 animate-pulse !text-transparent mt-1">
        &nbsp;
      </span>
    </div>
  </EachTimeline>
);

function primaryLine(row: TInvoiceActivityRow) {
  const desc = row.description?.toString().trim();
  if (desc) return desc;
  return getInvoiceActivityLabel(row.type);
}

const INITIAL_TIMELINE_LOG_COUNT = 3;

export default function InvoiceTimeline({ invoiceId }: Props) {
  const { t } = useTranslation();
  const tBase = "views.invoice.timeline";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [showMoreMap, setShowMoreMap] = React.useState<Record<string, boolean>>({});
  const [timelineExpanded, setTimelineExpanded] = React.useState(false);

  React.useEffect(() => {
    setTimelineExpanded(false);
  }, [invoiceId]);

  const query = useQuery<TInvoiceActivityRow[]>(
    ["invoice-activity", invoiceId],
    () => getInvoiceActivity(invoiceId),
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const sortedRows = React.useMemo(() => {
    const rows = filterBundledInvoiceSendFieldChanges(query.data || []);
    const sorted = [...rows].sort(compareInvoiceActivityTimelineRows);
    return mergeLineItemReplacePairs(sorted);
  }, [query.data]);

  const visibleRows = React.useMemo(
    () =>
      timelineExpanded ? sortedRows : sortedRows.slice(0, INITIAL_TIMELINE_LOG_COUNT),
    [sortedRows, timelineExpanded]
  );
  const hasMoreLogs = sortedRows.length > INITIAL_TIMELINE_LOG_COUNT;
  const hiddenLogCount = Math.max(0, sortedRows.length - INITIAL_TIMELINE_LOG_COUNT);
  const showPeek =
    !query.isLoading && !query.isError && hasMoreLogs && !timelineExpanded;
  const peekRow = showPeek ? sortedRows[INITIAL_TIMELINE_LOG_COUNT] : null;

  function renderActivityRow(row: TInvoiceActivityRow, isPeek: boolean) {
    const Icon = pickTimelineIcon(row);
    const friendly = getInvoiceActivityFriendly(row);
    const main = friendly?.primary || primaryLine(row);

    const descKey = `desc:${row.id}`;
    const showMore = showMoreMap[descKey] ?? false;
    const descTrunc = truncate(main, isPeek ? 96 : 220);
    const displayMain = isPeek
      ? descTrunc.text
      : showMore || !descTrunc.truncated
        ? main
        : descTrunc.text;

    const diff =
      isPeek || friendly?.hideDiff ? null : buildDiff(row.previous_value, row.new_value);
    const named = row.user?.toString()?.trim() || "";
    const userDisplay = named || (row.fk_user_id == null ? tr("System") : "");
    const byLine = userDisplay ? `${tr("by")} ${userDisplay}` : null;

    const detailLines = friendly?.detailLines ?? [];
    const detailLinesToRender = isPeek ? detailLines.slice(0, 1) : detailLines;

    return (
      <EachTimeline key={isPeek ? `peek-${row.id}` : row.id}>
        <Icon />
        <div>
          <span>{displayMain}</span>
          <span>{dateFormatter(row.timestamp)}</span>
          {byLine ? <span>{byLine}</span> : null}

          {detailLinesToRender.map((line, idx) => (
            <span key={`${row.id}:d:${isPeek ? "p:" : ""}${idx}`} className="break-all">
              {renderDetailLine(line)}
            </span>
          ))}

          {!isPeek && descTrunc.truncated && (
            <span>
              <button
                type="button"
                className="text-[0.8rem] text-slate-600 underline bg-transparent border-0 p-0 cursor-pointer"
                onClick={() => setShowMoreMap((m) => ({ ...m, [descKey]: !showMore }))}
              >
                {showMore ? tr("Show less") : tr("Show more")}
              </button>
            </span>
          )}

          {!isPeek &&
            diff?.map((d) => (
              <span key={d.path} className="break-all">
                {humanizeDiffPath(d.path)}: {formatDiffDisplayValue(d.path, d.previous)} →{" "}
                {formatDiffDisplayValue(d.path, d.next)}
              </span>
            ))}
        </div>
      </EachTimeline>
    );
  }

  return (
    <div>
      <div className="pt-4 pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2>{tr("Timeline")}</h2>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!query.isLoading && !query.isError && hasMoreLogs && (
              <SubtleButton
                type="button"
                primary
                aria-expanded={timelineExpanded}
                aria-label={
                  !timelineExpanded
                    ? t(`${tBase}.timelineExpandButtonAriaCollapsed`, { count: hiddenLogCount })
                    : undefined
                }
                onClick={() => setTimelineExpanded((e) => !e)}
              >
                {timelineExpanded ? (
                  <>
                    <span>{tr("Show less")}</span>
                    <RiArrowUpSLine className="text-base shrink-0 opacity-90" aria-hidden />
                  </>
                ) : (
                  <>
                    <span>{tr("Show more")}</span>
                    <RiArrowDownSLine className="text-base shrink-0 opacity-90" aria-hidden />
                  </>
                )}
              </SubtleButton>
            )}
            <SubtleButton onClick={() => query.refetch()} disabled={query.isFetching}>
              {tr("Refresh")}
            </SubtleButton>
          </div>
        </div>
      </div>

      <div
        className="pb-4"
        role={sortedRows.length > 0 ? "region" : undefined}
        aria-label={sortedRows.length > 0 ? tr("Timeline") : undefined}
      >
        {query.isLoading && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {!query.isLoading && query.isError && (
          <div className="px-2 py-4">
            <div className="text-sm text-red-600">{tr("Failed to load activity.")}</div>
            <div className="mt-3">
              <Button onClick={() => query.refetch()}>{tr("Try again")}</Button>
            </div>
          </div>
        )}

        {!query.isLoading && !query.isError && sortedRows.length === 0 && (
          <div className="px-2 py-2 text-sm text-slate-500">{tr("No activity yet.")}</div>
        )}

        {!query.isLoading &&
          !query.isError &&
          visibleRows.map((row) => renderActivityRow(row, false))}

        {!query.isLoading && !query.isError && peekRow && (
          <div
            className="relative -mt-1 max-h-[4.25rem] overflow-hidden pointer-events-none select-none"
            aria-hidden
          >
            <div className="opacity-[0.38]">{renderActivityRow(peekRow, true)}</div>
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-white/70 to-white"
              aria-hidden
            />
          </div>
        )}
      </div>
    </div>
  );
}

function renderDetailLine(line: string) {
  const match = line.match(/^payment ID:\s*(\d+)\s*$/i);
  if (match) {
    const id = match[1];
    return (
      <>
        {"payment ID: "}
        <NoStyleA className="!text-blue-700 !underline" to={`/payments/${id}`}>
          {id}
        </NoStyleA>
      </>
    );
  }
  return line;
}

